import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Copy, Share2, MessageCircle, Printer, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatINR, formatDate } from '../lib/format';
import { waLink, mergeTemplate } from '../lib/whatsapp';
import { TEMPLATES } from '../lib/content';

const STATUS_LABELS = { awaiting_advance: 'Awaiting Advance', partially_paid: 'Partially Paid', fully_paid: 'Fully Paid', overdue: 'Overdue' };

export default function InvoiceView() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [showPay, setShowPay] = useState(false);
  const [pay, setPay] = useState({ amount: 0, date: new Date().toISOString().slice(0,10), mode: 'UPI', reference: '' });

  const load = () => Promise.all([
    api.get(`/invoices/${id}`).then(r => setInv(r.data)),
    api.get('/settings').then(r => setSettings(r.data)),
    api.get('/reason-codes').then(r => setReasons(r.data)),
  ]);
  useEffect(() => { load(); }, [id]);

  if (!inv || !settings) return <div className="p-6 text-sm text-[var(--muted)]">Loading…</div>;

  const shareUrl = `${window.location.origin}/public/invoice/${id}`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success('Link copied'); };
  const balanceMsg = mergeTemplate(TEMPLATES.find(t => t.key==='balance_reminder').body, {
    client_name: inv.client_name, event_date: formatDate(inv.event_date), balance: formatINR(inv.balance)
  }) + `\n\nInvoice: ${shareUrl}`;
  const waSend = () => window.open(waLink(inv.client_phone, balanceMsg), '_blank');
  const copyMsg = () => { navigator.clipboard.writeText(balanceMsg); toast.success('Message copied'); };

  const addPayment = async () => {
    await api.post(`/invoices/${id}/payments`, pay);
    toast.success('Payment recorded');
    setShowPay(false); setPay({ amount: 0, date: new Date().toISOString().slice(0,10), mode: 'UPI', reference: '' });
    load();
  };

  return (
    <div className="px-4 py-5 lg:px-10 lg:py-8 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap print:hidden">
        <Link to="/invoices" className="eyebrow text-[var(--gold)]">← Invoices</Link>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowPay(true)} className="btn-ghost" data-testid="inv-add-payment">+ Payment</button>
          <Link to={`/invoices/${id}/edit`} className="btn-ghost"><Edit size={14}/> Edit</Link>
        </div>
      </div>

      <InvoiceDocument inv={inv} settings={settings} reasons={reasons} statusLabels={STATUS_LABELS}/>

      {/* Fixed action bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 print:hidden" style={{background:'var(--cream)', borderTop:'1px solid var(--line)', padding:'12px max(16px, env(safe-area-inset-left)) max(12px, env(safe-area-inset-bottom))'}}>
        <div className="max-w-3xl mx-auto flex gap-2 flex-wrap justify-center">
          <button onClick={() => window.print()} className="btn-ghost text-xs" data-testid="inv-print"><Printer size={12}/> Save PDF</button>
          <button onClick={waSend} className="btn-gold text-xs" data-testid="inv-wa-send"><MessageCircle size={12}/> Share on WhatsApp</button>
          <button onClick={copyLink} className="btn-ghost text-xs" data-testid="inv-copy-link"><Copy size={12}/> Copy link</button>
          <button onClick={copyMsg} className="btn-ghost text-xs" data-testid="inv-copy-msg"><Copy size={12}/> Copy message</button>
        </div>
      </div>

      {(inv.payments||[]).length > 0 && (
        <div className="mt-6 card-cream p-4 print:hidden">
          <div className="eyebrow mb-2">Payments</div>
          {inv.payments.map(p => (
            <div key={p.id} className="flex justify-between text-sm py-2 border-b hairline last:border-0">
              <div>{formatDate(p.date)} · {p.mode}{p.reference?` · ${p.reference}`:''}</div>
              <div className="mono-num">{formatINR(p.amount)}</div>
            </div>
          ))}
        </div>
      )}

      {showPay && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
          <div className="w-full max-w-md card-cream p-6 space-y-3">
            <h3 className="serif text-[22px]">Record Payment</h3>
            <div><label className="eyebrow">Amount</label><input type="number" value={pay.amount} onChange={e => setPay({...pay, amount: parseFloat(e.target.value||0)})} data-testid="pay-amount"/></div>
            <div><label className="eyebrow">Date</label><input type="date" value={pay.date} onChange={e => setPay({...pay, date: e.target.value})}/></div>
            <div><label className="eyebrow">Mode</label>
              <select value={pay.mode} onChange={e => setPay({...pay, mode: e.target.value})}>
                <option>UPI</option><option>Bank Transfer</option><option>Cash</option><option>Card</option>
              </select>
            </div>
            <div><label className="eyebrow">Reference</label><input value={pay.reference} onChange={e => setPay({...pay, reference: e.target.value})}/></div>
            <div className="flex gap-2"><button className="btn-ghost flex-1" onClick={() => setShowPay(false)}>Cancel</button><button className="btn-gold flex-1" onClick={addPayment} data-testid="pay-save">Save</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

export function InvoiceDocument({ inv, settings, reasons = [], statusLabels }) {
  const brandShort = settings.brand_name.split('—')[0].trim();
  const docText = settings.document_text || {};
  // Determine if using grouped structure
  const groups = (inv.event_groups && inv.event_groups.length) ? inv.event_groups : null;
  const reasonLabel = (code) => (reasons.find(r => r.code === code)?.label) || code;

  return (
    <div className="doc card-cream overflow-hidden" data-testid="invoice-doc">
      <div className="inv-head flex justify-between gap-6" style={{display:'flex'}}>
        <div>
          <div className="inv-brand-name">{brandShort}</div>
          <div className="inv-brand-tag">{settings.tagline}</div>
          <div className="inv-brand-meta">
            {settings.location_line}<br/>
            {settings.phone} &nbsp;·&nbsp; {settings.instagram}<br/>
            {settings.email}
          </div>
        </div>
        <div className="inv-doc-type text-right">
          <h1>Invoice</h1>
          <div className="inv-num">{inv.number}<br/>Issued {formatDate(inv.issued_on)}</div>
        </div>
      </div>

      <div className="inv-status">
        <span>{statusLabels?.[inv.status] || inv.status}</span>
        <span>{inv.balance_due_note || docText.balance_due_note_default}</span>
      </div>

      <div className="inv-body">
        <div className="inv-cols">
          <div>
            <div className="label">Billed To</div>
            <p className="name">{inv.client_name}</p>
            {inv.client_address && <p className="sub">{inv.client_address}</p>}
            {inv.client_phone && <p className="sub">{inv.client_phone}</p>}
          </div>
          <div>
            <div className="label">Event Details</div>
            {groups ? (
              groups.map(g => (
                <div key={g.id} className="mb-2">
                  <p><b>{g.name}</b> — {formatDate(g.date)}</p>
                  {g.reporting_time && <p className="sub">Reporting {g.reporting_time}{g.ready_by_time?` · Ready ${g.ready_by_time}`:''}</p>}
                  {g.venue && <p className="sub">{g.venue}</p>}
                </div>
              ))
            ) : (
              <>
                <p><b>{formatDate(inv.event_date)}</b></p>
                {inv.reporting_time && <p className="sub">Reaching time — {inv.reporting_time}</p>}
                {inv.ready_by_time && <p className="sub">Ready by — {inv.ready_by_time}</p>}
                {inv.venue && <p className="sub">{inv.venue}</p>}
              </>
            )}
            {inv.needs_confirmation && <p className="sub" style={{color:'var(--pending)'}}>⚠ {inv.confirmation_note}</p>}
          </div>
        </div>

        {/* Grouped multi-event or flat */}
        {groups ? (
          <div>
            {groups.map(g => <EventGroupRender key={g.id} group={g} reasonLabel={reasonLabel}/>)}
          </div>
        ) : (
          <table className="inv-table">
            <thead><tr><th>Service</th><th className="r">Qty</th><th className="r">Amount</th></tr></thead>
            <tbody>
              {(inv.services||[]).map((s, i) => (
                <tr key={i}>
                  <td>
                    <div className="svc">{s.name}</div>
                    {s.sub && <div className="svc-sub">{s.sub}</div>}
                    {s.is_complimentary && s.reason_code && <div className="svc-sub" style={{color:'var(--gold)'}}>Complimentary — {reasonLabel(s.reason_code)}</div>}
                  </td>
                  <td className="r">{s.qty}</td>
                  <td className="r">{s.was && <span className="was">{formatINR(s.was)}</span>}<span className="now">{formatINR(s.rate)}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {(inv.includes||[]).length > 0 && (
          <div className="inv-includes">
            <div className="label">{docText.includes_heading || 'Included In This Booking'}</div>
            <ul>{inv.includes.map((x,i) => <li key={i}>{x}</li>)}</ul>
          </div>
        )}

        <div className="inv-pay">
          <div className="inv-paybox">
            <div className="row"><span>Subtotal</span><span className="mono-num">{formatINR(inv.subtotal)}</span></div>
            {inv.discount > 0 && <div className="row"><span>Discount</span><span className="mono-num">− {formatINR(inv.discount)}</span></div>}
            <div className="row total"><span>Total</span><span className="mono-num">{formatINR(inv.total)}</span></div>
            {inv.advance_received > 0 && <div className="row adv"><span>Advance received ({inv.advance_pct}%)</span><span className="mono-num">{formatINR(inv.advance_received)}</span></div>}
            <div className="row bal"><span>Balance Due</span><span className="mono-num">{formatINR(inv.balance)}</span></div>
            {inv.value_given_away > 0 && (
              <div className="row" style={{borderTop:'1px solid var(--line)', marginTop:8, paddingTop:8, color:'var(--muted)', fontSize:12}}>
                <span>{docText.value_given_line || 'Total value included at no charge'}</span>
                <span className="mono-num">{formatINR(inv.value_given_away)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Terms — from snapshot on invoice */}
        {(inv.terms||[]).length > 0 && (
          <div className="inv-terms">
            <div className="label">Terms &amp; Conditions</div>
            <div className="inv-terms-grid">
              {inv.terms.map((t, i) => (
                <div className="term" key={i}>
                  <b>{t.title}</b>
                  {(t.body||'').replace('{advance_pct}', inv.advance_pct).replace('{validity_days}', '')}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="inv-sign">
          <div><div className="line"/><p>Client Signature</p></div>
          <div><div className="line"/><p>For {brandShort}</p></div>
        </div>

        {inv.thank_you_note && <div className="inv-note">{inv.thank_you_note}</div>}
      </div>

      <div className="inv-foot">
        <div className="n">{settings.brand_name}</div>
        {settings.location_line} &nbsp;·&nbsp; {settings.phone} &nbsp;·&nbsp; {settings.instagram} &nbsp;·&nbsp; {settings.website}
      </div>
    </div>
  );
}

function EventGroupRender({ group, reasonLabel }) {
  const eventSub = (group.services||[]).reduce((s, l) => {
    // support both stored shape (rate=now, was=orig) and computed shape (now)
    if (l.now != null) return s + l.now;
    return s + (l.qty||0) * (l.rate||0);
  }, 0);
  const eventTravel = (group.travel_lines||[]).reduce((s, l) => s + Number(l.amount||0), 0);
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between border-b hairline pb-1 mb-2">
        <div><span className="serif text-[17px]">{group.name}</span> <span className="text-xs text-[var(--muted)] tracking-[0.14em] uppercase ml-2">{formatDate(group.date)}</span></div>
        {(group.reporting_time || group.venue) && <div className="text-xs text-[var(--muted)]">{group.reporting_time && `Reporting ${group.reporting_time}`}{group.ready_by_time && ` · Ready ${group.ready_by_time}`}{group.venue && ` · ${group.venue}`}</div>}
      </div>
      <table className="inv-table" style={{borderTop:0}}>
        <tbody>
          {(group.services||[]).map((s, i) => {
            const isComp = s.pricing_mode === 'complimentary' || s.is_complimentary || (s.now === 0 && (s.qty||0)*(s.rate||0) > 0);
            const now = s.now != null ? s.now : (s.qty||0) * (s.rate||0);
            const was = s.was || ((s.was_amount != null) ? s.was_amount : ((s.qty||0)*(s.rate||0)));
            const showStrike = (s.saving||0) > 0 || (was > now && was > 0);
            return (
              <tr key={i}>
                <td>
                  <div className="svc">{s.name}</div>
                  {s.sub && <div className="svc-sub">{s.sub}</div>}
                  {isComp && s.reason_code && <div className="svc-sub" style={{color:'var(--gold)'}}>Complimentary — {reasonLabel(s.reason_code)}</div>}
                </td>
                <td className="r">{s.qty}</td>
                <td className="r">
                  {showStrike && <span className="was">{formatINR(was)}</span>}
                  <span className="now">{formatINR(now)}</span>
                </td>
              </tr>
            );
          })}
          {(group.travel_lines||[]).map((t, i) => (
            <tr key={'t'+i}>
              <td><div className="svc-sub">Travel · {t.label}</div></td>
              <td className="r"></td>
              <td className="r"><span className="now">{formatINR(t.amount)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-right text-xs text-[var(--muted)] pt-2">Event subtotal <span className="mono-num" style={{color:'var(--charcoal)', marginLeft:8}}>{formatINR(eventSub + eventTravel)}</span></div>
    </div>
  );
}
